import React, { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Check, Copy } from "lucide-react";
import bookingData from "./data/bookingData.json";
import PageContainer from "./Components/PageContainer";
import { bookingService } from "../../services/booking.service";
import { persistBookingSummary, readStoredBookingSummary } from "./bookingFlowStorage";

const DEFAULT_SESSION_LINK = "https://tutoring.app/session/abc123xyz";
const AUSTRALIA_TIME_ZONE = "Australia/Sydney";
const PAYMENT_UI_STATES = {
  IDLE: "idle",
  CREATING_CHECKOUT: "creating_checkout",
  REDIRECTING: "redirecting",
  CONFIRMING_PAYMENT: "confirming_payment",
  PAYMENT_CONFIRMED: "payment_confirmed",
  PAYMENT_FAILED_OR_CANCELLED: "payment_failed_or_cancelled",
};
const SUCCESS_SPARKLES = [
  { left: "6%", top: "7%", size: 10, delay: "0s", duration: "6.2s", color: "#1ED4CF" },
  { left: "14%", top: "22%", size: 14, delay: "1.1s", duration: "7.1s", color: "#D7DDFF" },
  { left: "24%", top: "10%", size: 12, delay: "0.5s", duration: "5.8s", color: "#8FE9D4" },
  { left: "35%", top: "18%", size: 8, delay: "1.9s", duration: "6.8s", color: "#4A4DFF" },
  { left: "48%", top: "8%", size: 16, delay: "0.3s", duration: "7.4s", color: "#BCEFF3" },
  { left: "61%", top: "15%", size: 10, delay: "2.3s", duration: "6.1s", color: "#1ED4CF" },
  { left: "73%", top: "11%", size: 13, delay: "0.9s", duration: "7.8s", color: "#D7DDFF" },
  { left: "85%", top: "20%", size: 9, delay: "1.5s", duration: "5.9s", color: "#8FE9D4" },
  { left: "91%", top: "8%", size: 11, delay: "2.6s", duration: "6.6s", color: "#4A4DFF" },
  { left: "10%", top: "63%", size: 12, delay: "0.7s", duration: "7.2s", color: "#BCEFF3" },
  { left: "21%", top: "79%", size: 9, delay: "2.1s", duration: "6.5s", color: "#1ED4CF" },
  { left: "42%", top: "73%", size: 15, delay: "1.2s", duration: "7.6s", color: "#D7DDFF" },
  { left: "58%", top: "82%", size: 10, delay: "2.8s", duration: "6.3s", color: "#8FE9D4" },
  { left: "77%", top: "71%", size: 12, delay: "0.4s", duration: "7s", color: "#4A4DFF" },
  { left: "88%", top: "61%", size: 14, delay: "1.7s", duration: "6.9s", color: "#BCEFF3" },
];
const SUCCESS_COMETS = [
  { left: "9%", top: "16%", width: 78, rotate: "-24deg", delay: "0.2s", duration: "5.8s" },
  { left: "72%", top: "12%", width: 92, rotate: "18deg", delay: "1.4s", duration: "6.6s" },
  { left: "18%", top: "78%", width: 70, rotate: "22deg", delay: "2.1s", duration: "5.4s" },
  { left: "81%", top: "70%", width: 86, rotate: "-18deg", delay: "0.9s", duration: "6.1s" },
];
const SUCCESS_ORBS = [
  { left: "-6%", top: "-4%", size: 220, color: "rgba(188, 239, 243, 0.65)", blur: 10, duration: "11s" },
  { left: "74%", top: "-8%", size: 180, color: "rgba(215, 221, 255, 0.6)", blur: 12, duration: "13s" },
  { left: "68%", top: "64%", size: 240, color: "rgba(143, 233, 212, 0.35)", blur: 18, duration: "12s" },
];
const SUCCESS_SHOWER = [
  { left: "5%", delay: "0.15s", duration: "4.9s", size: 10, drift: "-28px", trail: 66, color: "#BCEFF3" },
  { left: "11%", delay: "1.1s", duration: "5.3s", size: 12, drift: "16px", trail: 72, color: "#1ED4CF" },
  { left: "18%", delay: "0.65s", duration: "4.6s", size: 8, drift: "-12px", trail: 54, color: "#D7DDFF" },
  { left: "25%", delay: "1.6s", duration: "5.5s", size: 11, drift: "22px", trail: 76, color: "#8FE9D4" },
  { left: "33%", delay: "0.3s", duration: "4.8s", size: 9, drift: "-18px", trail: 68, color: "#4A4DFF" },
  { left: "40%", delay: "1.9s", duration: "5.7s", size: 13, drift: "26px", trail: 84, color: "#BCEFF3" },
  { left: "48%", delay: "0.9s", duration: "5s", size: 10, drift: "-8px", trail: 62, color: "#1ED4CF" },
  { left: "56%", delay: "1.35s", duration: "4.7s", size: 12, drift: "18px", trail: 74, color: "#D7DDFF" },
  { left: "64%", delay: "0.45s", duration: "5.2s", size: 10, drift: "-24px", trail: 70, color: "#8FE9D4" },
  { left: "72%", delay: "1.75s", duration: "5.4s", size: 9, drift: "14px", trail: 64, color: "#4A4DFF" },
  { left: "81%", delay: "0.8s", duration: "4.85s", size: 11, drift: "-20px", trail: 78, color: "#BCEFF3" },
  { left: "89%", delay: "1.45s", duration: "5.1s", size: 8, drift: "12px", trail: 58, color: "#1ED4CF" },
  { left: "95%", delay: "0.2s", duration: "4.95s", size: 12, drift: "-14px", trail: 72, color: "#D7DDFF" },
];
const SUCCESS_BURSTS = [
  { left: "18%", top: "16%", size: 74, delay: "0.2s", duration: "3.9s", color: "rgba(188, 239, 243, 0.85)" },
  { left: "82%", top: "18%", size: 82, delay: "0.9s", duration: "4.2s", color: "rgba(143, 233, 212, 0.8)" },
  { left: "50%", top: "11%", size: 110, delay: "0.45s", duration: "4.4s", color: "rgba(215, 221, 255, 0.82)" },
];

const buildAvatarDataUri = (name = "Teacher") => {
  const safeName = String(name || "Teacher").trim() || "Teacher";
  const initial = safeName.charAt(0).toUpperCase();
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="128" height="128" viewBox="0 0 128 128">
      <defs>
        <linearGradient id="bg" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stop-color="#f97316" />
          <stop offset="100%" stop-color="#ef4444" />
        </linearGradient>
      </defs>
      <rect width="128" height="128" rx="24" fill="url(#bg)" />
      <text x="50%" y="52%" text-anchor="middle" dominant-baseline="middle" font-family="Arial, sans-serif" font-size="48" font-weight="700" fill="white">
        ${initial}
      </text>
    </svg>
  `;

  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

const parseSlotDate = (value) => {
  if (!value) return null;
  const parsedDate = new Date(value);
  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

const formatAustralianTime = (date) =>
  date.toLocaleTimeString("en-AU", {
    timeZone: AUSTRALIA_TIME_ZONE,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

const formatDateLabel = (value, fallback = "Date not available") => {
  const parsedDate = parseSlotDate(value);
  if (!parsedDate) return fallback;

  return parsedDate.toLocaleDateString("en-AU", {
    timeZone: AUSTRALIA_TIME_ZONE,
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

const formatTimeLabel = (slot = {}, fallback = "Time not available") => {
  if (slot?.time_label) return slot.time_label;

  const startDate = parseSlotDate(slot?.start_time || slot?.startTime);
  const endDate = parseSlotDate(slot?.end_time || slot?.endTime);
  if (!startDate || !endDate) return fallback;

  return `${formatAustralianTime(startDate)} - ${formatAustralianTime(endDate)}`;
};

const formatDurationLabel = (slot = {}, fallback = "60 minutes") => {
  const startDate = parseSlotDate(slot?.start_time || slot?.startTime);
  const endDate = parseSlotDate(slot?.end_time || slot?.endTime);
  if (!startDate || !endDate) return fallback;

  const durationInMinutes = Math.max(
    0,
    Math.round((endDate.getTime() - startDate.getTime()) / 60000)
  );

  return `${durationInMinutes || 60} minutes`;
};

const normalizeBookingId = (value) => String(value || "").trim();
const normalizeStatusValue = (value) =>
  String(value || "").trim().toLowerCase();

const findBookingById = (payload, bookingId) => {
  const normalizedBookingId = normalizeBookingId(bookingId);
  if (!normalizedBookingId) return null;

  const allBookings = [
    ...(payload?.upcoming || []),
    ...(payload?.past || []),
  ];

  return (
    allBookings.find(
      (booking) =>
        normalizeBookingId(booking?.booking_id || booking?.id) === normalizedBookingId
    ) || null
  );
};

const getSessionLink = (confirmResponse = {}, booking = {}, fallback = "") =>
  confirmResponse?.session_link ||
  confirmResponse?.sessionLink ||
  confirmResponse?.meeting_link ||
  confirmResponse?.meetingLink ||
  confirmResponse?.meeting_url ||
  confirmResponse?.meetingUrl ||
  confirmResponse?.booking?.session_link ||
  confirmResponse?.booking?.meeting_link ||
  booking?.session_link ||
  booking?.sessionLink ||
  booking?.meeting_link ||
  booking?.meetingLink ||
  booking?.meeting_url ||
  booking?.meetingUrl ||
  fallback ||
  DEFAULT_SESSION_LINK;

const buildConfirmedBookingView = ({
  confirmResponse,
  latestBooking,
  storedBookingSummary,
  studentName,
  fallbackTutorId,
}) => {
  const responseBooking = confirmResponse?.booking || null;
  const bookingRecord = latestBooking || responseBooking || {};
  const tutor =
    bookingRecord?.tutor ||
    responseBooking?.tutor ||
    storedBookingSummary?.tutor ||
    bookingData.tutor;
  const slot = bookingRecord?.slot || responseBooking?.slot || storedBookingSummary?.heldBooking?.slot;

  return {
    bookingId:
      normalizeBookingId(
        bookingRecord?.booking_id ||
          responseBooking?.booking_id ||
          storedBookingSummary?.bookingId
      ) || "",
    tutor: {
      ...bookingData.tutor,
      ...tutor,
      id: tutor?.id || fallbackTutorId || storedBookingSummary?.tutorId || "",
      name: tutor?.name || tutor?.full_name || bookingData.tutor?.name || "Tutor",
      image: tutor?.photoImage || tutor?.photo || tutor?.image || tutor?.tutor_image || "",
    },
    studentName,
    formattedDate:
      formatDateLabel(slot?.date || slot?.start_time, "") ||
      storedBookingSummary?.formattedDate ||
      "Date not available",
    selectedTime:
      formatTimeLabel(slot, "") ||
      storedBookingSummary?.selectedTime ||
      "Time not available",
    duration:
      formatDurationLabel(slot, "") ||
      storedBookingSummary?.duration ||
      "60 minutes",
    sessionLink: getSessionLink(confirmResponse, bookingRecord, storedBookingSummary?.sessionLink),
    heldBooking: bookingRecord || storedBookingSummary?.heldBooking || null,
    tutorId:
      tutor?.id || fallbackTutorId || storedBookingSummary?.tutorId || "",
    yearId: storedBookingSummary?.yearId || "",
    bookingStatus:
      normalizeStatusValue(
        bookingRecord?.status ||
          responseBooking?.status ||
          storedBookingSummary?.status
      ) || "",
    paymentStatus:
      normalizeStatusValue(
        bookingRecord?.payment_status ||
          responseBooking?.payment_status ||
          confirmResponse?.payment_status ||
          storedBookingSummary?.payment_status
      ) || "",
    paidAt:
      bookingRecord?.paid_at ||
      responseBooking?.paid_at ||
      confirmResponse?.paid_at ||
      storedBookingSummary?.paid_at ||
      "",
  };
};

const BookingConfirmed = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const hasTriggeredConfirmationRef = useRef(false);
  const { user } = useSelector((state) => state.user);
  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );
  const bookingId = normalizeBookingId(searchParams.get("bookingId"));
  const checkoutSessionId = String(
    searchParams.get("checkout_session_id") || searchParams.get("session_id") || ""
  ).trim();
  const tutorId = String(searchParams.get("tutorId") || "").trim();
  const storedBookingSummary = useMemo(
    () => readStoredBookingSummary(bookingId),
    [bookingId]
  );
  const studentName =
    storedBookingSummary?.studentName ||
    user?.user_name ||
    user?.full_name ||
    [user?.first_name, user?.last_name].filter(Boolean).join(" ") ||
    "Emma Wilson";
  const [paymentUiState, setPaymentUiState] = useState(
    PAYMENT_UI_STATES.IDLE
  );

  const confirmBookingMutation = useMutation({
    mutationFn: async () => {
      const confirmResponse = await bookingService.confirmBooking({
        booking_id: bookingId,
        checkout_session_id: checkoutSessionId,
      });

      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["student-class-booking-dashboard"],
        }),
        queryClient.invalidateQueries({
          queryKey: ["student-class-booking-list"],
        }),
      ]);

      let latestBooking = null;
      try {
        const bookingsPayload = await bookingService.getBookings();
        latestBooking = findBookingById(bookingsPayload, bookingId);
      } catch (_error) {
        latestBooking = null;
      }

      return { confirmResponse, latestBooking };
    },
  });

  useEffect(() => {
    if (!bookingId || !checkoutSessionId || hasTriggeredConfirmationRef.current) {
      if (!bookingId || !checkoutSessionId) {
        setPaymentUiState(PAYMENT_UI_STATES.PAYMENT_FAILED_OR_CANCELLED);
      }
      return;
    }

    hasTriggeredConfirmationRef.current = true;
    setPaymentUiState(PAYMENT_UI_STATES.CONFIRMING_PAYMENT);
    confirmBookingMutation.mutate();
  }, [bookingId, checkoutSessionId, confirmBookingMutation]);

  const confirmedBooking = useMemo(() => {
    if (!confirmBookingMutation.data) {
      return null;
    }

    return buildConfirmedBookingView({
      confirmResponse: confirmBookingMutation.data.confirmResponse,
      latestBooking: confirmBookingMutation.data.latestBooking,
      storedBookingSummary,
      studentName,
      fallbackTutorId: tutorId,
    });
  }, [confirmBookingMutation.data, storedBookingSummary, studentName, tutorId]);

  useEffect(() => {
    if (confirmedBooking) {
      persistBookingSummary({
        ...confirmedBooking,
        paymentAttemptStatus: "completed",
      });
      setPaymentUiState(PAYMENT_UI_STATES.PAYMENT_CONFIRMED);
    }
  }, [confirmedBooking]);

  useEffect(() => {
    if (confirmBookingMutation.isError && !confirmedBooking) {
      setPaymentUiState(PAYMENT_UI_STATES.PAYMENT_FAILED_OR_CANCELLED);
    }
  }, [confirmBookingMutation.isError, confirmedBooking]);

  const nextSteps = [
    {
      step: 1,
      title: "Confirmation Email",
      description:
        "You'll receive a confirmation email with all session details and the meeting link.",
    },
    {
      step: 2,
      title: "Preparation",
      description:
        "Your tutor will review your notes and prepare materials for the session.",
    },
    {
      step: 3,
      title: "Reminder",
      description:
        "We'll send you a reminder 24 hours and 1 hour before your session.",
    },
  ];

  const handleRetry = () => {
    if (!bookingId || !checkoutSessionId || confirmBookingMutation.isPending) {
      return;
    }

    confirmBookingMutation.reset();
    hasTriggeredConfirmationRef.current = true;
    setPaymentUiState(PAYMENT_UI_STATES.CONFIRMING_PAYMENT);
    confirmBookingMutation.mutate();
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(
        confirmedBooking?.sessionLink || DEFAULT_SESSION_LINK
      );
      toast.success("Session link copied.");
    } catch (_error) {
      toast.error("Could not copy the session link.");
    }
  };

  const missingParams = !bookingId || !checkoutSessionId;
  const avatarFallback = buildAvatarDataUri(confirmedBooking?.tutor?.name);
  const paidAtLabel = confirmedBooking?.paidAt
    ? new Date(confirmedBooking.paidAt).toLocaleString("en-AU", {
        timeZone: AUSTRALIA_TIME_ZONE,
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";
  const paymentBadgeLabel =
    confirmedBooking?.paymentStatus === "paid"
      ? "Paid"
      : confirmedBooking?.paymentStatus
        ? confirmedBooking.paymentStatus
        : "Confirmed";
  const bookingBadgeLabel =
    confirmedBooking?.bookingStatus === "confirmed"
      ? "Confirmed"
      : confirmedBooking?.bookingStatus || "Confirmed";

  if (missingParams) {
    return (
      <PageContainer className="pb-12 pt-8">
        <div className="mx-auto max-w-3xl px-4 md:px-8">
          <div className="rounded-2xl border border-red-200 bg-red-50 p-8 text-center text-red-700 shadow-sm">
            Missing booking confirmation details. Re-open this page from the Stripe success
            redirect or check your bookings dashboard.
          </div>
        </div>
      </PageContainer>
    );
  }

  if (
    paymentUiState === PAYMENT_UI_STATES.CONFIRMING_PAYMENT &&
    !confirmedBooking &&
    !confirmBookingMutation.isError
  ) {
    return (
      <PageContainer className="pb-12 pt-8">
        <div className="mx-auto max-w-3xl px-4 md:px-8">
          <div className="rounded-2xl border border-gray-200 bg-white p-8 text-center text-gray-700 shadow-sm">
            Finalizing your booking and syncing your Stripe payment...
          </div>
        </div>
      </PageContainer>
    );
  }

  if (confirmBookingMutation.isError && !confirmedBooking) {
    return (
      <PageContainer className="pb-12 pt-8">
        <div className="mx-auto max-w-3xl px-4 md:px-8">
          <div className="rounded-2xl border border-red-200 bg-red-50 p-8 text-center text-red-700 shadow-sm">
            <p>
              {confirmBookingMutation.error?.message ||
                "We could not confirm the booking payment."}
            </p>
            <button
              type="button"
              onClick={handleRetry}
              className="mt-5 rounded-full bg-[#1C1BB3] px-6 py-3 text-sm font-semibold text-white shadow-md hover:bg-[#171697]"
            >
              Retry Confirmation
            </button>
          </div>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer className="pb-12 pt-8">
      <div className="px-4 md:px-8 xl:px-10">
        <style>{`
          @keyframes bookingSparkleFloat {
            0% { transform: translate3d(0, 14px, 0) scale(0.7) rotate(0deg); opacity: 0; }
            12% { opacity: 0.95; }
            50% { transform: translate3d(0, -10px, 0) scale(1) rotate(18deg); opacity: 1; }
            100% { transform: translate3d(0, -34px, 0) scale(0.82) rotate(42deg); opacity: 0; }
          }

          @keyframes bookingSparklePulse {
            0%, 100% { transform: scale(0.65); opacity: 0.5; }
            50% { transform: scale(1.05); opacity: 1; }
          }

          @keyframes bookingSparkleShower {
            0% { transform: translate3d(0, -70px, 0) scale(0.35); opacity: 0; }
            12% { opacity: 1; }
            72% { transform: translate3d(var(--sparkle-drift), 240px, 0) scale(1); opacity: 1; }
            100% { transform: translate3d(calc(var(--sparkle-drift) * 1.2), 360px, 0) scale(0.78); opacity: 0; }
          }

          @keyframes bookingSparkleTrail {
            0%, 100% { opacity: 0; transform: scaleY(0.2); }
            25% { opacity: 0.75; transform: scaleY(0.78); }
            70% { opacity: 0.35; transform: scaleY(1); }
          }

          @keyframes bookingCometSweep {
            0% { transform: translate3d(-20px, 10px, 0) scaleX(0.7) rotate(var(--comet-rotate)); opacity: 0; }
            16% { opacity: 0.95; }
            55% { transform: translate3d(18px, -18px, 0) scaleX(1) rotate(var(--comet-rotate)); opacity: 1; }
            100% { transform: translate3d(36px, -30px, 0) scaleX(0.78) rotate(var(--comet-rotate)); opacity: 0; }
          }

          @keyframes bookingOrbDrift {
            0%, 100% { transform: translate3d(0, 0, 0) scale(1); }
            50% { transform: translate3d(16px, -18px, 0) scale(1.08); }
          }

          @keyframes bookingHaloBreath {
            0%, 100% { transform: scale(0.92); opacity: 0.45; }
            50% { transform: scale(1.08); opacity: 0.9; }
          }

          @keyframes bookingBadgeLift {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-5px); }
          }

          @keyframes bookingTitleRise {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-3px); }
          }

          @keyframes bookingBurstBloom {
            0% { transform: translate3d(0, 0, 0) scale(0.3); opacity: 0; }
            20% { opacity: 1; }
            65% { transform: translate3d(0, -10px, 0) scale(1); opacity: 0.9; }
            100% { transform: translate3d(0, -18px, 0) scale(1.18); opacity: 0; }
          }

          @keyframes bookingPanelGlow {
            0%, 100% { box-shadow: 0 26px 54px rgba(28, 27, 179, 0.09), 0 0 0 1px rgba(255, 255, 255, 0.72) inset; }
            50% { box-shadow: 0 34px 70px rgba(30, 212, 207, 0.16), 0 0 0 1px rgba(255, 255, 255, 0.9) inset; }
          }
        `}</style>
        <div
          className="relative mx-auto w-full max-w-7xl overflow-hidden rounded-2xl border-2 border-white/90 bg-[rgba(244,255,251,0.2)] p-6 shadow-xl backdrop-blur-[3px] md:p-8"
          style={{ animation: "bookingPanelGlow 5.2s ease-in-out infinite" }}
        >
          <div className="pointer-events-none absolute inset-0">
            <div className="absolute inset-x-10 top-0 h-40 bg-[radial-gradient(circle_at_top,rgba(188,239,243,0.65),transparent_65%)]" />
            <div className="absolute inset-x-0 top-8 h-48 bg-[radial-gradient(circle_at_center,rgba(215,221,255,0.34),transparent_68%)]" />
            <div className="absolute inset-x-[8%] top-0 h-56 bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.95),rgba(188,239,243,0.18)_42%,transparent_74%)]" />
            {SUCCESS_ORBS.map((orb, index) => (
              <span
                key={`${orb.left}-${orb.top}-${index}`}
                className="absolute rounded-full"
                style={{
                  left: orb.left,
                  top: orb.top,
                  width: `${orb.size}px`,
                  height: `${orb.size}px`,
                  background: orb.color,
                  filter: `blur(${orb.blur}px)`,
                  animation: `bookingOrbDrift ${orb.duration} ease-in-out infinite`,
                }}
              />
            ))}
            {SUCCESS_COMETS.map((comet, index) => (
              <span
                key={`${comet.left}-${comet.top}-${index}`}
                className="absolute block overflow-hidden rounded-full"
                style={{
                  left: comet.left,
                  top: comet.top,
                  width: `${comet.width}px`,
                  height: "12px",
                  opacity: 0,
                  "--comet-rotate": comet.rotate,
                  animation: `bookingCometSweep ${comet.duration} ease-in-out infinite`,
                  animationDelay: comet.delay,
                }}
              >
                <span
                  className="absolute inset-0 rounded-full"
                  style={{
                    background:
                      "linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(188,239,243,0.85) 28%, rgba(74,77,255,0.95) 62%, rgba(255,255,255,0) 100%)",
                    boxShadow: "0 0 18px rgba(188, 239, 243, 0.8)",
                  }}
                />
              </span>
            ))}
            {SUCCESS_BURSTS.map((burst, index) => (
              <span
                key={`${burst.left}-${burst.top}-${index}`}
                className="absolute block rounded-full"
                style={{
                  left: burst.left,
                  top: burst.top,
                  width: `${burst.size}px`,
                  height: `${burst.size}px`,
                  marginLeft: `-${burst.size / 2}px`,
                  marginTop: `-${burst.size / 2}px`,
                  animation: `bookingBurstBloom ${burst.duration} ease-out infinite`,
                  animationDelay: burst.delay,
                }}
              >
                <span
                  className="absolute inset-0 rounded-full"
                  style={{
                    background: `radial-gradient(circle, ${burst.color} 0%, rgba(255,255,255,0.78) 24%, rgba(255,255,255,0) 72%)`,
                    filter: "blur(1px)",
                  }}
                />
                <span className="absolute left-1/2 top-1/2 h-[2px] w-full -translate-x-1/2 -translate-y-1/2 rounded-full bg-white/90" />
                <span className="absolute left-1/2 top-1/2 h-full w-[2px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-white/90" />
                <span className="absolute left-1/2 top-1/2 h-[2px] w-[78%] -translate-x-1/2 -translate-y-1/2 rotate-45 rounded-full bg-white/80" />
                <span className="absolute left-1/2 top-1/2 h-[2px] w-[78%] -translate-x-1/2 -translate-y-1/2 -rotate-45 rounded-full bg-white/80" />
              </span>
            ))}
            {SUCCESS_SPARKLES.map((sparkle, index) => (
              <span
                key={`${sparkle.left}-${sparkle.top}-${index}`}
                className="absolute block rounded-full"
                style={{
                  left: sparkle.left,
                  top: sparkle.top,
                  width: `${sparkle.size}px`,
                  height: `${sparkle.size}px`,
                  animation: `bookingSparkleFloat ${sparkle.duration} ease-in-out infinite`,
                  animationDelay: sparkle.delay,
                }}
              >
                <span
                  className="absolute inset-0 rounded-full"
                  style={{
                    background: `radial-gradient(circle, ${sparkle.color} 0%, rgba(255,255,255,0.98) 42%, rgba(255,255,255,0) 74%)`,
                    boxShadow: `0 0 16px ${sparkle.color}`,
                    animation: "bookingSparklePulse 1.8s ease-in-out infinite",
                  }}
                />
                <span
                  className="absolute left-1/2 top-1/2 block -translate-x-1/2 -translate-y-1/2 rounded-full bg-white/90"
                  style={{
                    width: `${Math.max(2, sparkle.size * 0.2)}px`,
                    height: `${sparkle.size * 1.9}px`,
                  }}
                />
                <span
                  className="absolute left-1/2 top-1/2 block -translate-x-1/2 -translate-y-1/2 rounded-full bg-white/90"
                  style={{
                    width: `${sparkle.size * 1.9}px`,
                    height: `${Math.max(2, sparkle.size * 0.2)}px`,
                  }}
                />
              </span>
            ))}
            {SUCCESS_SHOWER.map((sparkle, index) => (
              <span
                key={`${sparkle.left}-${sparkle.delay}-${index}`}
                className="absolute top-0 block"
                style={{
                  left: sparkle.left,
                  width: `${sparkle.size}px`,
                  height: `${sparkle.size}px`,
                  "--sparkle-drift": sparkle.drift,
                  animation: `bookingSparkleShower ${sparkle.duration} cubic-bezier(0.2, 0.72, 0.24, 1) infinite`,
                  animationDelay: sparkle.delay,
                }}
              >
                <span
                  className="absolute left-1/2 top-0 block -translate-x-1/2 rounded-full"
                  style={{
                    width: "2px",
                    height: `${sparkle.trail}px`,
                    background: `linear-gradient(180deg, rgba(255,255,255,0.92) 0%, ${sparkle.color} 48%, rgba(255,255,255,0) 100%)`,
                    transformOrigin: "top center",
                    filter: "blur(0.6px)",
                    animation: "bookingSparkleTrail 1.5s ease-in-out infinite",
                  }}
                />
                <span
                  className="absolute left-1/2 top-[54px] block -translate-x-1/2 rounded-full"
                  style={{
                    width: `${sparkle.size}px`,
                    height: `${sparkle.size}px`,
                    background: `radial-gradient(circle, rgba(255,255,255,1) 0%, ${sparkle.color} 45%, rgba(255,255,255,0) 75%)`,
                    boxShadow: `0 0 18px ${sparkle.color}`,
                  }}
                />
                <span
                  className="absolute left-1/2 top-[54px] block -translate-x-1/2 -translate-y-1/2 rounded-full bg-white/90"
                  style={{
                    width: `${Math.max(2, sparkle.size * 0.22)}px`,
                    height: `${sparkle.size * 2.2}px`,
                  }}
                />
                <span
                  className="absolute left-1/2 top-[54px] block -translate-x-1/2 -translate-y-1/2 rounded-full bg-white/90"
                  style={{
                    width: `${sparkle.size * 2.2}px`,
                    height: `${Math.max(2, sparkle.size * 0.22)}px`,
                  }}
                />
              </span>
            ))}
          </div>

          <div className="relative flex flex-col items-center pt-4 text-center">
            <div className="relative flex h-24 w-24 items-center justify-center rounded-full bg-[#DDF7E3] shadow-[0_0_0_10px_rgba(221,247,227,0.35)]" style={{ animation: "bookingBadgeLift 3.6s ease-in-out infinite" }}>
              <span
                className="absolute inset-[-10px] rounded-full border border-[#8FE9D4]/60"
                style={{ animation: "bookingHaloBreath 2.6s ease-in-out infinite" }}
              />
              <span
                className="absolute inset-[-22px] rounded-full bg-[radial-gradient(circle,rgba(188,239,243,0.28),rgba(188,239,243,0)_72%)]"
                style={{ animation: "bookingHaloBreath 3.8s ease-in-out infinite" }}
              />
              <div className="relative z-10 flex h-14 w-14 items-center justify-center rounded-full border-[3px] border-[#0DA340] bg-white/90 text-[#0DA340] shadow-lg">
                <Check size={24} strokeWidth={3} />
              </div>
            </div>

            <h1 className="mt-6 bg-gradient-to-r from-[#0DA340] via-[#08A595] to-[#1C1BB3] bg-clip-text text-2xl font-semibold text-transparent md:text-3xl" style={{ animation: "bookingTitleRise 3.2s ease-in-out infinite" }}>
              Booking Confirmed!
            </h1>
            <p className="mt-3 max-w-2xl text-base text-gray-800 md:text-lg">
              Your tutoring session has been successfully booked
            </p>
          </div>

          <div className="relative mt-10 rounded-2xl border border-[#1ED4CF] bg-white p-6 shadow-md">
            <div className="mb-5 flex flex-wrap gap-3">
              <span className="rounded-full border border-green-200 bg-green-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.12em] text-green-700">
                Payment: {paymentBadgeLabel}
              </span>
              <span className="rounded-full border border-[#C9D5FF] bg-[#EEF2FF] px-3 py-1 text-xs font-semibold uppercase tracking-[0.12em] text-[#1C1BB3]">
                Booking: {bookingBadgeLabel}
              </span>
              {paidAtLabel ? (
                <span className="rounded-full border border-[#BCEFF3] bg-[#F2FDFF] px-3 py-1 text-xs font-semibold uppercase tracking-[0.12em] text-[#0F766E]">
                  Paid at {paidAtLabel}
                </span>
              ) : null}
            </div>
            <div className="grid gap-6 md:grid-cols-[8rem_minmax(0,1fr)_10rem] md:items-start">
              <img
                src={confirmedBooking?.tutor?.image}
                alt={confirmedBooking?.tutor?.name}
                className="h-32 w-32 rounded-xl object-cover"
                onError={(event) => {
                  event.currentTarget.onerror = null;
                  event.currentTarget.src = avatarFallback;
                }}
              />

              <div className="space-y-4">
                <div>
                  <p className="text-base text-gray-500">Your tutor</p>
                  <p className="mt-1 text-xl font-medium text-gray-900">
                    {confirmedBooking?.tutor?.name}
                  </p>
                </div>

                <div>
                  <p className="text-base text-gray-500">Student</p>
                  <p className="mt-1 text-xl font-medium text-gray-900">
                    {confirmedBooking?.studentName}
                  </p>
                </div>

                <div>
                  <p className="text-base text-gray-500">Date & Time</p>
                  <p className="mt-1 text-xl font-medium text-gray-900">
                    {confirmedBooking?.formattedDate} at {confirmedBooking?.selectedTime}
                  </p>
                </div>
              </div>

              <div>
                <p className="text-base text-gray-500">Duration</p>
                <p className="mt-1 text-xl font-medium text-gray-900">
                  {confirmedBooking?.duration}
                </p>
              </div>
            </div>
          </div>

          <div className="relative mt-8 rounded-2xl border border-[#C9D5FF] bg-[#E9EEFF] p-6 shadow-md">
            <div className="flex-1">
              <h2 className="text-xl font-semibold text-gray-900">
                Online Session Link
              </h2>

              <div className="mt-5 flex flex-col gap-4 lg:flex-row lg:items-center">
                <div className="flex-1 rounded-xl border border-[#D9E2FF] bg-white px-4 py-3 text-base text-[#4A4DFF]">
                  {confirmedBooking?.sessionLink || DEFAULT_SESSION_LINK}
                </div>

                <button
                  type="button"
                  onClick={handleCopyLink}
                  className="inline-flex h-12 items-center justify-center gap-2 rounded-xl bg-[#1C1BB3] px-6 text-base font-semibold text-white shadow-md hover:bg-[#171697]"
                >
                  <Copy size={18} />
                  Copy Link
                </button>
              </div>

              <p className="mt-4 text-base text-gray-600">
                This link will also be sent to your email and will be active
                10 minutes before the session.
              </p>
            </div>
          </div>

          <div className="relative mt-14 rounded-2xl bg-white p-6 shadow-md">
            <h2 className="text-2xl font-semibold text-gray-900">
              What happens next?
            </h2>

            <div className="mt-6 space-y-6">
              {nextSteps.map((item) => (
                <div key={item.step} className="flex items-start gap-4">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#D7DDFF] text-base font-semibold text-[#1C1BB3]">
                    {item.step}
                  </div>

                  <div>
                    <h3 className="text-lg font-medium text-gray-900">
                      {item.title}
                    </h3>
                    <p className="mt-1.5 text-base text-gray-600">
                      {item.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-center pb-2 pt-10">
            <button
              type="button"
              onClick={() => navigate("/classBooking/dashboard")}
              className="rounded-full bg-[#1C1BB3] px-12 py-3 text-lg font-semibold text-white shadow-md hover:bg-[#171697]"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    </PageContainer>
  );
};

export default BookingConfirmed;
